// The "RMARTN Cust Pays" reply family and the RMA number as its Reference ID
// (2026-10-27, docs/pipeline-behaviors.md "RMARTN Cust Pays: a customer-paid
// Website return"; docs/contact-view.md "The RMA number in the reply").
//
// A customer-paid return on a 1A or AM Website order is answered with one of
// the client's RMARTN templates:
//   - "RMARTN Cust Pays": the customer does not say they need a different or
//     correct part;
//   - "RMARTN Cust Pays Do Carry": they need a different part and we carry
//     it (INSERTREASON, INSERTWARRANTY, INSERTLINK);
//   - "RMARTN Cust Pays Don't Carry" (AM "Don’t", 1A "Dont"): we don't.
// Titles are matched by folder and normalized title, so the apostrophe and
// the spelling never matter (rmartnCustPaysVariant()).
//
// These templates end with "Your Reference ID #:" (AM "Reference ID #:"):
// the RMA's own number, which exists only once the return is processed. The
// draft keeps RMA_REFERENCE_TOKEN there (shown in the reply box as a chip,
// RMA_REFERENCE_CHIP); Process fills it with the RMA's erpOrderId. It counts
// as a placeholder for the reply-content Send checks
// (findUnresolvedPlaceholders()) and is never sent as text: the server fills
// it, or an empty Reference ID line, from the order number as a last resort.
//
// Pure: no calls. Used by the server (pipeline.js, agent-draft.js,
// send-approved-reply.js) and the panel (SuggestionPanel.jsx).

import { templateTitle } from './template-keys.js'

export const RMA_REFERENCE_TOKEN = '[[RMA_REFERENCE]]'
export const RMA_REFERENCE_CHIP = 'RMA # — filled in when the return is processed'
// Plain Send while the token is in the reply and the return has not run.
export const RMA_REFERENCE_NOT_PROCESSED = 'Process the return first so the reply has the RMA number.'
// How long Process all (and Send after a processed return) waits for the
// RMA's erpOrderId before it falls back to the order number.
export const RMA_REFERENCE_WAIT_MS = 5000
export const RMA_REFERENCE_FALLBACK_NOTE = "Reference ID used the order number: the RMA number wasn't ready in 5 s"
// The server's final check at send (send-approved-reply.js).
export const REFERENCE_ID_FILLED_AT_SEND_NOTE = 'Reference ID filled with the order number at send: the reply had no RMA number there'

// The task choice when CXHub can't tell whether we carry the correct part.
export const CARRY_CHOICE_KIND = 'rmartn_carry'
export const CARRY_YES = 'carry_yes'
export const CARRY_NO = 'carry_no'
export const CARRY_CHOICE_LABEL = 'Do we carry the correct part?'
export const CARRY_CHOICE_OPTIONS = Object.freeze([
  Object.freeze({ value: CARRY_YES, label: 'Yes' }),
  Object.freeze({ value: CARRY_NO, label: 'No' }),
])
export const PART_LINK_STEP_TYPE = 'rmartn_part_link'
export const PART_LINK_LABEL = 'Link to the correct part'

export const RMARTN_VARIANTS = Object.freeze({ CUST_PAYS: 'cust_pays', DO_CARRY: 'do_carry', DONT_CARRY: 'dont_carry', MORE_INFO: 'more_info' })
// Each variant's template key (2026-11-02, template-keys.js); the titles are
// the registry's Website aliases.
export const RMARTN_TEMPLATE_KEYS = Object.freeze({
  cust_pays: 'rmartn_cust_pays',
  do_carry: 'rmartn_cust_pays_do_carry',
  dont_carry: 'rmartn_cust_pays_dont_carry',
  more_info: 'rmartn_cust_pays_more_info',
})
export const RMARTN_TITLES = Object.freeze(Object.fromEntries(Object.entries(RMARTN_TEMPLATE_KEYS).map(([v, key]) => [v, templateTitle(key, 'website')])))
const VARIANT_BY_KEY = new Map(Object.entries(RMARTN_TEMPLATE_KEYS).map(([v, key]) => [key, v]))

// Lower case, curly apostrophes and the apostrophe itself dropped, dashes
// and spaces as one space: "RMARTN Cust Pays Don’t Carry" and "RMARTN Cust
// Pays Dont Carry" are the same title.
export function normalizeRmartnTitle(title) {
  return String(title ?? '')
    .toLowerCase()
    .replace(/[’‘'`´]/g, '')
    .replace(/[–—-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const VARIANT_BY_TITLE = new Map(Object.entries(RMARTN_TITLES).map(([k, t]) => [normalizeRmartnTitle(t), k]))

// 'cust_pays' | 'do_carry' | 'dont_carry' | 'more_info' | null.
export function rmartnCustPaysVariant(title) {
  return VARIANT_BY_TITLE.get(normalizeRmartnTitle(title)) ?? null
}

// Whether a title is one of the RMARTN Cust Pays family, whose Reference ID
// is the RMA number.
export function isRmartnCustPaysTitle(title) {
  return rmartnCustPaysVariant(title) != null
}

// The template of a variant among a folder's templates ([{ title, folder,
// ... }]), by normalized title; folder (optional) must match too.
export function findRmartnTemplate(templates, variant, { folder = null } = {}) {
  return (templates ?? []).find((t) => (t?.key ? VARIANT_BY_KEY.get(t.key) ?? null : rmartnCustPaysVariant(t?.title)) === variant && (!folder || String(t?.folder ?? '').toLowerCase() === String(folder).toLowerCase())) ?? null
}

// --- Do we carry the correct part? ------------------------------------------

const fitOf = (p) => String(p?.fit ?? '').toLowerCase().replace(/\s+/g, '_')

// From find_part's candidates (agent-tools.js; fit 'fits' / 'check_fitment' /
// 'does_not_fit', or the words 'fits' / 'check fitment' / 'does not fit'):
//   - exactly one candidate fits: 'do_carry' with that part;
//   - the search ran and found nothing that fits or might fit: 'dont_carry';
//   - anything else (no search, several fits, a "check fitment"): null, and
//     the reply step gets the Yes / No choice.
// searched: find_part was called. { decision, part, why }.
export function carryDecision(parts, { searched = false } = {}) {
  const list = Array.isArray(parts) ? parts.filter(Boolean) : []
  const fits = list.filter((p) => fitOf(p) === 'fits')
  const maybe = list.filter((p) => fitOf(p) === 'check_fitment')
  if (fits.length === 1 && !maybe.length) return { decision: RMARTN_VARIANTS.DO_CARRY, part: fits[0], why: 'one part in our catalog fits the vehicle' }
  if (fits.length === 1) return { decision: null, part: null, why: 'one part fits and another needs its fitment checked' }
  if (fits.length > 1) return { decision: null, part: null, why: 'several parts fit the vehicle' }
  if (maybe.length) return { decision: null, part: null, why: 'a part needs its fitment checked' }
  if (searched) return { decision: RMARTN_VARIANTS.DONT_CARRY, part: null, why: 'no part in our catalog fits the vehicle' }
  return { decision: null, part: null, why: 'the correct part was not looked up' }
}

// The SKU in a 1A or AM product link: the /i/<sku> path segment, upper
// case ("https://www.1aauto.com/front-hub/i/1ASHF00123" -> "1ASHF00123").
// null when the link is not ours or has no such segment (an AM
// /products/ii/<amSku>.html link names AM's own SKU, not ours: the panel
// then asks for the SKU).
export function skuFromPartLink(link) {
  const s = String(link ?? '').trim()
  if (!s) return null
  let url
  try { url = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`) } catch { return null }
  if (!/(^|\.)(1aauto\.com|am-autoparts\.com)$/i.test(url.hostname)) return null
  const parts = url.pathname.split('/').filter(Boolean)
  const at = parts.findIndex((p) => p.toLowerCase() === 'i')
  if (at < 0 || !parts[at + 1]) return null
  const sku = decodeURIComponent(parts[at + 1]).replace(/\.html?$/i, '').trim()
  return /^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(sku) ? sku.toUpperCase() : null
}

// --- Filling the template ----------------------------------------------------

const isHtml = (t) => /<[a-z][^>]*>/i.test(String(t ?? ''))
const escapeHtml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
const linkHtml = (l) => `<a href="${escapeHtml(l)}" target="_blank" rel="noopener noreferrer">${escapeHtml(l)}</a>`

// INSERTREASON, INSERTWARRANTY and INSERTLINK, where a value is given. The
// link is a real link in an HTML (Website email) reply, a written-out URL in
// plain text. A value not given leaves its token for the person.
export function fillCarryTokens(text, { reason = null, warranty = null, link = null } = {}) {
  let out = String(text ?? '')
  const html = isHtml(out)
  const r = typeof reason === 'string' ? reason.trim().replace(/[.\s]+$/, '') : ''
  if (r) out = out.replace(/INSERTREASON/g, () => (html ? escapeHtml(r) : r))
  const w = typeof warranty === 'string' ? warranty.trim() : ''
  if (w) out = out.replace(/INSERTWARRANTY/g, () => (html ? escapeHtml(w) : w))
  const l = typeof link === 'string' ? link.trim() : ''
  if (l) out = out.replace(/INSERTLINK(?![A-Z0-9])/g, () => (html ? linkHtml(l) : l))
  return out
}

// A link or warranty filled earlier, swapped for a new value in the reply
// (the person edits "Link to the correct part"): the old value where it is,
// else the token. Unchanged when neither is there.
export function replaceCarryValue(text, kind, previous, next) {
  const out = String(text ?? '')
  const value = typeof next === 'string' ? next.trim() : ''
  if (!value) return out
  const html = isHtml(out)
  const token = kind === 'link' ? 'INSERTLINK' : 'INSERTWARRANTY'
  const prev = typeof previous === 'string' ? previous.trim() : ''
  if (prev && prev !== value) {
    if (kind === 'link' && html && out.includes(linkHtml(prev))) return out.split(linkHtml(prev)).join(linkHtml(value))
    const prevShown = html ? escapeHtml(prev) : prev
    if (out.includes(prevShown)) return out.split(prevShown).join(html ? escapeHtml(value) : value)
  }
  if (out.includes(token)) return fillCarryTokens(out, kind === 'link' ? { link: value } : { warranty: value })
  return out
}

// --- The refund amounts --------------------------------------------------------

// The refund breakdown lines set to the step's current amounts (2026-10-27):
// "Original Refund", "Return Label Deduction" (or "Label Deduction") and
// "Final Refund", whatever their value is now (an INSERT token or an amount
// filled earlier), so a deduction the person edits updates the reply. The
// template's own "$" is kept, one added where it has none. An amount that
// is null leaves its line as it is. amounts: { original, deduction, final }.
const AMOUNT_LINES = [
  { key: 'original', re: String.raw`Original\s+Refund(?:\s+Amount)?` },
  { key: 'deduction', re: String.raw`(?:Return\s+)?Label\s+Deduction` },
  { key: 'final', re: String.raw`Final\s+Refund(?:\s+(?:Amount|Total))?` },
]
const AMOUNT_GAP = String.raw`((?:\s|&nbsp;|<[^>]*>|[:$\-–—])*?)`
const AMOUNT_VALUE = String.raw`(INSERT[A-Z0-9]+(?![A-Z0-9])|\d[\d,]*(?:\.\d{1,2})?)`
const money2 = (n) => (typeof n === 'number' && Number.isFinite(n) ? n.toFixed(2) : null)

export function setRefundAmountLines(text, amounts = {}) {
  let out = String(text ?? '')
  for (const { key, re } of AMOUNT_LINES) {
    const v = money2(amounts?.[key])
    if (v == null) continue
    out = out.replace(new RegExp(String.raw`\b(${re})\b${AMOUNT_GAP}${AMOUNT_VALUE}`, 'gi'), (m, label, gap) => `${label}${gap}${gap.includes('$') ? '' : '$'}${v}`)
  }
  return out
}

// Whether the reply carries the three breakdown lines (an RMARTN Cust Pays
// reply): the panel's cue to keep them in step with the return step.
export function hasRefundBreakdownLines(text) {
  const s = String(text ?? '')
  return AMOUNT_LINES.every(({ re }) => new RegExp(String.raw`\b${re}\b`, 'i').test(s))
}

// --- The Reference ID line ----------------------------------------------------

const REFERENCE_LABEL_SOURCE = String.raw`(?:Your\s+)?Reference\s+ID\s*#:`

export function hasRmaReferenceToken(text) {
  return String(text ?? '').includes(RMA_REFERENCE_TOKEN)
}

// The Reference ID line with the token as its value. HTML: right after the
// label and its closing inline tags ("<strong>Your Reference ID #:</strong>
// [[RMA_REFERENCE]]"); plain text: the line's value is replaced. Unchanged
// when there is no Reference ID line or the token is there already.
export function withRmaReferenceToken(text) {
  const s = String(text ?? '')
  if (hasRmaReferenceToken(s)) return s
  if (isHtml(s)) {
    const re = new RegExp(String.raw`(${REFERENCE_LABEL_SOURCE})((?:\s*<\/(?:strong|b|em|span)>)*)([^<\n]*)`, 'i')
    return re.test(s) ? s.replace(re, (m, label, closing) => `${label}${closing} ${RMA_REFERENCE_TOKEN}`) : s
  }
  const re = new RegExp(String.raw`^([ \t]*${REFERENCE_LABEL_SOURCE})[^\n]*$`, 'im')
  return re.test(s) ? s.replace(re, (m, label) => `${label} ${RMA_REFERENCE_TOKEN}`) : s
}

// The token replaced by the value (the RMA's erpOrderId, or the order's).
export function fillRmaReference(text, value) {
  const v = typeof value === 'string' ? value.trim() : (value != null ? String(value) : '')
  if (!v) return String(text ?? '')
  const out = String(text ?? '')
  return out.split(RMA_REFERENCE_TOKEN).join(isHtml(out) ? escapeHtml(v) : v)
}

// The reply with the token taken out (for the generic placeholder checks
// while the token has its own rule).
export function withoutRmaReferenceToken(text) {
  return String(text ?? '').split(RMA_REFERENCE_TOKEN).join('')
}

// A "Reference ID #:" line with nothing after it, in the visible text.
export function hasEmptyReferenceIdLine(text) {
  const visible = String(text ?? '')
    .replace(/<br\s*\/?>|<\/(?:p|li|div|h[1-6]|tr)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
  return new RegExp(String.raw`^[ \t]*${REFERENCE_LABEL_SOURCE}[ \t]*$`, 'im').test(visible)
}

// The send-time fill (send-approved-reply.js): the token, and an empty
// Reference ID line, get the value. { text, filled }.
export function fillReferenceIdAtSend(text, value) {
  let out = String(text ?? '')
  const v = typeof value === 'string' ? value.trim() : ''
  if (!v) return { text: out, filled: false }
  let filled = false
  if (hasRmaReferenceToken(out)) { out = fillRmaReference(out, v); filled = true }
  if (hasEmptyReferenceIdLine(out)) {
    if (isHtml(out)) {
      const re = new RegExp(String.raw`(${REFERENCE_LABEL_SOURCE})((?:\s*<\/(?:strong|b|em|span)>)*)(\s*)(?=<\/p>|<br|$)`, 'gi')
      out = out.replace(re, (m, label, closing) => `${label}${closing} ${escapeHtml(v)}`)
    } else {
      out = out.replace(new RegExp(String.raw`^([ \t]*${REFERENCE_LABEL_SOURCE})[ \t]*$`, 'gim'), (m, label) => `${label} ${v}`)
    }
    filled = true
  }
  return { text: out, filled }
}

// The index of the return step that makes the RMA (process_return_refund),
// or -1.
export function rmaReferenceStepIndex(actions = []) {
  return (actions ?? []).findIndex((a) => a?.type === 'process_return_refund')
}

// The token's state for the panel: { hasToken, stepIndex, processed,
// erpRmaId }. processed: the return step ran (stepStates: the panel's, by
// index); erpRmaId: its result's RMA number, when it has one.
export function rmaReferenceState(reply, actions = [], stepStates = {}) {
  const hasToken = hasRmaReferenceToken(reply)
  const stepIndex = rmaReferenceStepIndex(actions)
  const st = stepIndex >= 0 ? stepStates?.[stepIndex] : null
  const processed = st?.status === 'success'
  const erpRmaId = processed ? (st?.result?.erpRmaId ?? null) : null
  return { hasToken, stepIndex, processed, erpRmaId: erpRmaId ? String(erpRmaId) : null }
}
